import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { motion } from "framer-motion";
import { 
  Share2, MessageCircle, TrendingUp, Sparkles, 
  Zap, ArrowRight, Play, Star, Users
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CustomCursor from "../components/CustomCursor";
import Process from "../components/Process";
import SEO from "../components/SEO";
import SocialWorkshop from "../components/SocialWorkshop";
import SectionStepper from "../components/SectionStepper";
import ReelsShowcase from "../components/ReelsShowcase";
import SocialPostsShowcase from "../components/SocialPostsShowcase";

const socialSteps = [
  {
    step: "01",
    title: "Brand Voice",
    desc: "Defining how your brand speaks and interacts on social platforms to build a cohesive persona.",
    image: "https://images.unsplash.com/photo-1611162617474-5b21e879e113?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "02",
    title: "Content Strategy",
    desc: "Planning a mix of educational, inspirational, and promotional content tailored for each platform.",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "03",
    title: "Production",
    desc: "Creating high-end reels, posts, and stories that capture attention in a crowded feed.",
    image: "https://images.unsplash.com/photo-1551818255-e6e10975bc17?auto=format&fit=crop&q=80&w=1200"
  },
  {
    step: "04",
    title: "Community Management",
    desc: "Active engagement and growth strategies to turn followers into loyal brand advocates.",
    image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&q=80&w=1200"
  }
];

const SocialMedia = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = new Lenis({
      duration: 1.15,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true
    });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    requestAnimationFrame(raf);
    return () => lenis.destroy();
  }, []);

  const stepperSections = [
    { id: "hero", label: "Story" },
    { id: "capabilities", label: "Capabilities" },
    { id: "works", label: "Featured" },
    { id: "process", label: "Blueprint" },
    { id: "workshop", label: "Viral Lab" }
  ];

  return (
    <div className="bg-[var(--ink)] text-[var(--cream)] min-h-screen">
      <SEO 
        title="Social Media & Content Strategy" 
        description="Transform your social presence with cinematic content and data-driven engagement strategies."
      />
      {/* <CustomCursor /> */}
      <SectionStepper sections={stepperSections} />
      <div className="grain" />
      <Navbar />

      <main>
        {/* Hero */}
        <section id="hero" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-transparent to-[var(--ink)] z-10" />
            <motion.div
              initial={{ scale: 1.1, opacity: 0 }}
              animate={{ scale: 1, opacity: 0.55 }}
              transition={{ duration: 1.5 }}
              className="w-full h-full"
            >
              <video 
                autoPlay 
                muted 
                loop 
                playsInline 
                preload="auto"
                className="w-full h-full object-cover grayscale brightness-75"
              >
                <source src="https://videos.pexels.com/video-files/34953967/14806517_1920_1080_50fps.mp4" type="video/mp4" />
              </video>
            </motion.div>
          </div>

          <div className="container-xl w-full relative z-20 px-6">
            <motion.div
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="max-w-5xl"
            >
              <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-sm mb-6 block">
                Digital Engagement
              </span>
              <h1 className="font-display text-6xl md:text-8xl lg:text-9xl mb-8 leading-tight">
                Turn Scroll <br />
                into <span className="italic font-serif-accent gold-text">Story</span>
              </h1>
              <p className="max-w-2xl text-[var(--cream-muted)] text-lg md:text-xl mb-12 font-body leading-relaxed">
                In a world of noise, we help you be the signal. We combine cinematic video production with strategic distribution to build thriving digital communities.
              </p>
              <div className="flex flex-col md:flex-row items-start justify-start gap-6">
                <Link to="/contact" className="btn-primary">
                  Grow Your Influence <ArrowRight className="w-5 h-5 ml-2" />
                </Link>
                <a href="#workshop" className="btn-ghost">
                  Enter Workshop
                </a>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Grid */}
        <section id="capabilities" className="py-32 relative">
          <div className="container-xl px-6">
            <Kicker index="01" label="Capabilities" />
            <h2 className="mt-8 font-display text-4xl md:text-6xl text-[var(--cream)] mb-20">
              Strategic <span className="italic gold-text">Growth</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: <Share2 />, title: "Content Production", desc: "High-end reels, photography, and graphics tailored for social." },
                { icon: <Users />, title: "Community Management", desc: "Building and nurturing relationships with your audience." },
                { icon: <TrendingUp />, title: "Growth Strategy", desc: "Data-driven approaches to expand your reach and impact." }
              ].map((s, i) => (
                <div key={i} className="p-10 bg-[var(--ink-2)] border border-[var(--rule)] rounded-2xl hover:border-[var(--gold)] transition-all duration-500 group">
                  <div className="text-[var(--gold)] mb-6 transform group-hover:scale-110 transition-transform origin-left">{s.icon}</div>
                  <h3 className="font-display text-2xl mb-4">{s.title}</h3>
                  <p className="text-[var(--muted)] text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ReelsShowcase />
        <SocialPostsShowcase />

        <Process id="process" steps={socialSteps} title="The Social Blueprint" kicker={{ index: "03", label: "The Strategy" }} />

        <SocialWorkshop />
      </main>

      <Footer />
    </div>
  );
};

const Kicker = ({ index, label }) => (
  <div className="flex items-center gap-4 mb-4">
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--gold)]">{index}</span>
    <span className="w-16 h-px bg-[var(--rule)]" />
    <span className="font-impact text-[11px] tracking-[0.4em] text-[var(--cream-muted)] uppercase">{label}</span>
  </div>
);

export default SocialMedia;
